import moment from "moment";
import {
  dateTimeFormatted,
  formatAppointmentDateTime,
  formattedDate,
} from "../utils/dateUtils";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { preventDuplicateRequest } from "../utils/requestManager";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export interface AssignedUser {
  userId: number;
  username: string;
  fullname: string;
  emailId: string;
  departmentName: string | null;
  genderName: string | null;
  phoneNumber: string | null;
  userTypeName: string;
  userRoleName: string;
  teamName: string | null;
}

export interface UpdateAssignedToRequest {
  subjectQmeScheduleIDs: string;
  assignTo: number;
}

interface DocumentCategoryItem {
  documentTypeId: number | string;
  documentType: string;
  documentCategory: string;
  documentTypeDescription: string;
}

interface DocumentTypeOption {
  value: string;
  label: string;
  documentCategory: string;
  documentTypeDescription: string;
}

const formatDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

const editor = process.env.REACT_APP_CASE_URL || "";
if (!editor) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const EditorAPI = api(editor);

const getEditorCardSearch = async (editorData: any): Promise<any> => {
  const key = `getEditorCardSearch-${JSON.stringify(editorData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await EditorAPI.post<ApiResponse<any>>(
        "/api/editor/summary",
        editorData,
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const getEditorSimpleSearch = async (editorData: any): Promise<any> => {
  const key = `getEditorSimpleSearch-${JSON.stringify(editorData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await EditorAPI.post<ApiResponse<any>>(
        "/api/editor/schedule",
        editorData,
      );
      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        subjectqmescheduleid: data.subjectQmeScheduleID,
        appointmentDateTime: formatAppointmentDateTime(data.appointmentDateTime),
        dueDate: formattedDate(data.dueDate),
        format_dueDate: formatAppointmentDateTime(data.dueDate),
      }));
      console.log(updatedResult, "updatedResult");

      return updatedResult;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const GetAssignedTo = async (): Promise<
  ApiResponse<AssignedUser[]> | undefined
> => {
  try {
    const response = await EditorAPI.get(`/api/editor/assignee/list`);
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const updateAssignedTo = async (
  data: UpdateAssignedToRequest,
): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.put<ApiResponse<any>>(
      "/api/editor/assignee/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error("Unable to update assigned To. Please try again later.");
  }
};

const editorReportStatusUpdate = async (
  data: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.put<ApiResponse<any>>(
      "/api/editor/report-status/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      "Unable to update report-status To. Please try again later.",
    );
  }
};

const downloadZipFileForDictations = async (
  subjectQmeScheduleIds: any,
): Promise<void> => {
  try {
    const response = await EditorAPI.get(
      `/api/dictation/downloadAll?subjectQmeScheduleIds=${subjectQmeScheduleIds}`,
      {
        responseType: "blob",
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
const addDWCExtension = async (payload: any) => {
  try {
    const response = await EditorAPI.post(
      "/api/editor/dwc-extension/add",
      payload,
    );
    return response.data;
  } catch (error: any) {
    console.error("Error saving DWC extension:", error);
    throw error;
  }
};
const getDwcExtensionDetails = async (subjectQmeScheduleID: number) => {
  try {
    const response = await EditorAPI.get(`/api/editor/dwc-extension/details?`, {
      params: {
        subjectQmeScheduleID: subjectQmeScheduleID,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching DWC extension details:", error);
    throw error;
  }
};

const updateDwcExtension = async (payload: any) => {
  try {
    const response = await EditorAPI.post(
      "/api/editor/dwc-extension/update",
      payload,
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating DWC extension:", error);
    throw error;
  }
};

const fetchEditorModelPopupDetails = async (subjectQmeScheduleID: Number) => {
  try {
    const response = await EditorAPI.get(
      `api/editor/dictation/details?subjectQmeScheduleID=${subjectQmeScheduleID}`,
    );
    return response.data;
  } catch (error: any) {
    console.error("Error while fetching EditorModelPopupDetails:", error);
    throw error;
  }
};

const downloadSingleFileForDictationsPopup = async (
  requestDictationAttachmentId: any,
): Promise<void> => {
  try {
    const response = await EditorAPI.get(
      `/api/dictation/download?requestDictationAttachmentId=${requestDictationAttachmentId}`,
      {
        responseType: "blob",
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
const getDWCExtensionLogs = async (
  subjectQmeScheduleID: number,
): Promise<any> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any[]>>(
      `api/editor/dwc-extension/logs?subjectQmeScheduleID=${subjectQmeScheduleID}`,
    );

    return response.data; // return whole response
  } catch (error) {
    console.error("Error fetching logs:", error);
    throw error;
  }
};

const getEditorDetailsByQMEID = async (
  subjectQMEScheduleId: any,
): Promise<any> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any>>(
      `/api/editor/status/details?subjectQMEScheduleId=${subjectQMEScheduleId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      createdDate: moment(data.createdDate).format("MM/DD/YYYY hh:mm A"),
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getEditorRecordListSummary = async (
  caseInfoId: number,
  qmeScheduleId: number,
): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any>>(
      `/api/editor/record-summary/list?caseInfoId=${caseInfoId}&qmeScheduleId=${qmeScheduleId}`,
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getEditorRecordList = async (
  caseInfoId: number,
  senderKeyId: number,
  qmeScheduleId: number,
): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any>>(
      `/api/editor/record/list?caseInfoId=${caseInfoId}&senderKeyId=${senderKeyId}&qmeScheduleId=${qmeScheduleId}`,
    );

    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => ({
          ...item,
          id: item.caseRecordDetailsID,
          uploadDateTimeNew: dateTimeFormatted(item.uploadDateTime),
          pagesReviewed: item.pageReviewed,
          downloadROR: item.cloudLocation,
          fileAction: item.totalNoOfFiles,
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const GetNotesCategoryByUser = async () => {
  try {
    const response = await EditorAPI.get(`/api/editor/notes/categories/list`);
    const result = response.data?.result || [];

    return result.map((data: any) => ({
      label: data.notesType,
      value: data.notesCategoryId,
    }));
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const getEditorStatusLogsList = async (
  subjectQmeScheduleId: Number,
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await EditorAPI.get(
      `/api/editor/status/details?subjectQMEScheduleId=${subjectQmeScheduleId}`,
    );

    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => ({
          ...item,
          doctorQMEStatusName: item.editorStatusName,
          createdDateNew: dateTimeFormatted(item.createdDate),
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getEditorQMEDoctorRecordsEmail = async (
  subjectQMEScheduleId: any,
): Promise<any> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any>>(
      `/api/editor/email/final-report-completed?subjectQmeScheduleId=${subjectQMEScheduleId}`,
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getEditorExtensionEmail = async (
  subjectQMEScheduleId: any,
  dwcExtensionStatus: any,
): Promise<any> => {
  try {
    const response = await EditorAPI.get<ApiResponse<any>>(
      `/api/editor/email/dwc-extension?subjectQmeScheduleId=${subjectQMEScheduleId}&dwcExtensionStatus=${dwcExtensionStatus}`,
    );

    return response;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const fetchEditorDocumentTypeByAppointment = async (
  subjectQmeScheduleId: any,
): Promise<DocumentTypeOption[]> => {
  try {
    const response = await EditorAPI.get(
      `/api/editor/document/by-appointment-type?subjectQmeScheduleId=${subjectQmeScheduleId}`,
    );
    const list = response.data?.result.map((item: DocumentCategoryItem) => ({
      value: item.documentTypeId,
      label: item.documentType,
      documentCategory: item.documentCategory,
      documentTypeDescription: item.documentTypeDescription,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export {
  getEditorCardSearch,
  getEditorSimpleSearch,
  GetAssignedTo,
  editorReportStatusUpdate,
  updateAssignedTo,
  downloadZipFileForDictations,
  addDWCExtension,
  getDwcExtensionDetails,
  updateDwcExtension,
  fetchEditorModelPopupDetails,
  downloadSingleFileForDictationsPopup,
  getDWCExtensionLogs,
  getEditorDetailsByQMEID,
  getEditorRecordListSummary,
  getEditorRecordList,
  GetNotesCategoryByUser,
  getEditorStatusLogsList,
  getEditorQMEDoctorRecordsEmail,
  getEditorExtensionEmail,
  fetchEditorDocumentTypeByAppointment,
};
